/* PinLayer.tsx — click-anywhere comment pins (BugHerd-style). */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFeedback } from './store';
import { SEVERITIES } from './constants';
import type { Severity, Ticket } from './types';

type Composer =
  | { kind: 'new'; x: number; y: number }
  | { kind: 'view'; x: number; y: number; ticketId: string };

type PinSurface = {
  rect: { left: number; top: number; width: number; height: number };
  scrollLeft: number;
  scrollTop: number;
};

function getCanvasSurfaceElement(): HTMLElement | null {
  const canvas = document.querySelector('.v-canvas') as HTMLElement | null;
  if (canvas) {
    const position = window.getComputedStyle(canvas).position;
    if (!position || position === 'static') {
      canvas.style.position = 'relative';
    }
  }
  return canvas;
}

function getPinSurface(): PinSurface {
  const canvas = getCanvasSurfaceElement();
  if (canvas) {
    const rect = canvas.getBoundingClientRect();
    return {
      rect,
      scrollLeft: canvas.scrollLeft,
      scrollTop: canvas.scrollTop,
    };
  }

  return {
    rect: { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight },
    scrollLeft: window.scrollX,
    scrollTop: window.scrollY,
  };
}

function clientPointToSurfacePoint(clientX: number, clientY: number) {
  const surface = getPinSurface();
  return {
    x: clientX - surface.rect.left + surface.scrollLeft,
    y: clientY - surface.rect.top + surface.scrollTop,
  };
}

function surfacePointToViewportPoint(x: number, y: number) {
  const surface = getPinSurface();
  return {
    left: surface.rect.left + x - surface.scrollLeft,
    top: surface.rect.top + y - surface.scrollTop,
  };
}

export function PinLayer() {
  const {
    tickets,
    addTicket,
    addComment,
    removeTicket,
    commentMode,
    setCommentMode,
    reporter,
    route,
    routeLabel,
  } = useFeedback();

  const layerRef = useRef<HTMLDivElement>(null);
  const [composer, setComposer] = useState<Composer | null>(null);
  const [, setSurfaceTick] = useState(0);
  const openComposerAtViewportPoint = useCallback((clientX: number, clientY: number) => {
    const point = clientPointToSurfacePoint(clientX, clientY);
    setComposer({
      kind: 'new',
      x: point.x,
      y: point.y,
    });
  }, []);

  // Crosshair cursor on <body> while commenting
  useEffect(() => {
    document.body.classList.toggle('fbtk-commenting', commentMode);
    return () => document.body.classList.remove('fbtk-commenting');
  }, [commentMode]);

  const pins = useMemo(
    () => tickets.filter((t) => t.kind === 'pin' && t.route === route).slice().reverse(),
    [tickets, route],
  );
  const canvas = typeof document !== 'undefined' ? getCanvasSurfaceElement() : null;

  useEffect(() => {
    getCanvasSurfaceElement();
  }, [route]);

  useEffect(() => {
    if (!composer) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setComposer(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [composer]);

  useEffect(() => {
    const onDescribeWithoutArea = () => {
      openComposerAtViewportPoint(
        Math.min(Math.max(window.innerWidth / 2, 180), Math.max(window.innerWidth - 180, 180)),
        Math.min(Math.max(window.innerHeight / 2, 220), Math.max(window.innerHeight - 180, 220)),
      );
    };
    window.addEventListener('fbtk:describe-without-area', onDescribeWithoutArea);
    return () => window.removeEventListener('fbtk:describe-without-area', onDescribeWithoutArea);
  }, [openComposerAtViewportPoint]);

  useEffect(() => {
    const refresh = () => setSurfaceTick((tick) => tick + 1);
    const canvas = getCanvasSurfaceElement();
    window.addEventListener('resize', refresh);
    window.addEventListener('scroll', refresh, { passive: true });
    canvas?.addEventListener('scroll', refresh, { passive: true });
    return () => {
      window.removeEventListener('resize', refresh);
      window.removeEventListener('scroll', refresh);
      canvas?.removeEventListener('scroll', refresh);
    };
  }, [route]);

  if (!commentMode && pins.length === 0 && !composer) return null;

  const onLayerClick = (e: React.MouseEvent) => {
    if (!commentMode) return;
    const el = e.target as HTMLElement;
    if (el.closest('.proto-pin-dot') || el.closest('.proto-pin-composer')) return;
    openComposerAtViewportPoint(e.clientX, e.clientY);
  };

  const renderPin = (t: Ticket, i: number, renderInCanvas = false) => {
    const point = renderInCanvas
      ? { left: t.x ?? 0, top: t.y ?? 0 }
      : surfacePointToViewportPoint(t.x ?? 0, t.y ?? 0);
    return (
      <div
        key={t.id}
        className="proto-pin-dot"
        data-feedback-pin
        data-feedback-pin-id={t.id}
        style={{ left: point.left, top: point.top }}
        onClick={(e) => {
          e.stopPropagation();
          setComposer({ kind: 'view', x: t.x ?? 0, y: t.y ?? 0, ticketId: t.id });
        }}
        title={t.title}
        aria-label={`Pin ${i + 1}: ${t.title}`}
      >
        {i + 1}
      </div>
    );
  };

  return (
    <div
      ref={layerRef}
      className={'proto-pin-layer' + (commentMode ? ' on' : '')}
      onClick={onLayerClick}
    >
      {canvas
        ? createPortal(
            <div className="proto-pin-canvas-layer">{pins.map((t, i) => renderPin(t, i, true))}</div>,
            canvas,
          )
        : pins.map((t, i) => renderPin(t, i))}

      {composer && (
        <PinComposer
          composer={composer}
          ticket={
            composer.kind === 'view' ? tickets.find((t) => t.id === composer.ticketId) ?? null : null
          }
          reporter={reporter}
          onClose={() => setComposer(null)}
          onCreate={(payload) => {
            const t = addTicket({
              kind: 'pin',
              source: 'pin',
              tag: 'comment',
              severity: payload.severity,
              note: payload.note,
              title: payload.note.slice(0, 60),
              route,
              routeLabel,
              x: composer.x,
              y: composer.y,
            });
            setComposer({ kind: 'view', x: composer.x, y: composer.y, ticketId: t.id });
          }}
          onAddComment={(text) => {
            if (composer.kind === 'view') addComment(composer.ticketId, { text, who: reporter || 'anonymous' });
          }}
          onDelete={() => {
            if (composer.kind === 'view' && confirm('Delete this pin and its thread?')) {
              removeTicket(composer.ticketId);
              setComposer(null);
            }
          }}
        />
      )}

      {commentMode && !composer && (
        <div className="proto-pin-hint">
          <span>
            Click anywhere to drop a comment · <span className="kbd">Esc</span> to exit
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              openComposerAtViewportPoint(
                Math.min(Math.max(window.innerWidth / 2, 180), Math.max(window.innerWidth - 180, 180)),
                Math.min(Math.max(window.innerHeight / 2, 220), Math.max(window.innerHeight - 180, 220)),
              );
            }}
          >
            Describe without area
          </button>
          <button onClick={() => setCommentMode(false)} aria-label="Exit comment mode">
            Done
          </button>
        </div>
      )}
    </div>
  );
}

function PinComposer({
  composer,
  ticket,
  reporter,
  onClose,
  onCreate,
  onAddComment,
  onDelete,
}: {
  composer: Composer;
  ticket: Ticket | null;
  reporter: string;
  onClose: () => void;
  onCreate: (p: { note: string; severity: Severity }) => void;
  onAddComment: (text: string) => void;
  onDelete: () => void;
}) {
  const isNew = composer.kind === 'new' || !ticket;
  const [note, setNote] = useState('');
  const [severity, setSeverity] = useState<Severity>('medium');
  const [reply, setReply] = useState('');
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setNote('');
    setReply('');
    const id = setTimeout(() => taRef.current?.focus(), 30);
    return () => clearTimeout(id);
  }, [composer]);

  const COMPOSER_W = 320;
  const COMPOSER_H = 280;
  const point = surfacePointToViewportPoint(composer.x, composer.y);
  const viewportX = point.left;
  const viewportY = point.top;
  const flipX = viewportX + 30 + COMPOSER_W > window.innerWidth - 280;
  const flipY = viewportY + COMPOSER_H > window.innerHeight - 80;
  const left = flipX ? point.left - COMPOSER_W - 12 : point.left + 22;
  const top = flipY ? Math.max(12, point.top - COMPOSER_H + 30) : point.top - 8;

  if (isNew) {
    return (
      <div
        className="proto-pin-composer"
        data-feedback-pin-composer
        style={{ left, top, width: COMPOSER_W }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ppc-head">
          <span className="ppc-mono">NEW PIN</span>
          <button className="ppc-x" onClick={onClose} aria-label="Cancel">
            ×
          </button>
        </div>
        <div className="ppc-body">
          <div className="ppc-row-label">SEVERITY</div>
          <div className="ppc-sev-row">
            {SEVERITIES.map((s) => (
              <button
                key={s.id}
                onClick={() => setSeverity(s.id)}
                className={severity === s.id ? 'on' : ''}
                style={{ ['--dot' as string]: s.dot }}
                aria-label={s.label}
              >
                <span className="dot" />
                <span>{s.label}</span>
              </button>
            ))}
          </div>
          <textarea
            ref={taRef}
            aria-label="Debug issue description"
            placeholder="What's wrong here? What would you change?"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && note.trim())
                onCreate({ note: note.trim(), severity });
            }}
          />
          <div className="ppc-foot">
            <span className="ppc-mono">⌘↵ to save</span>
            <button className="ppc-cancel" onClick={onClose}>
              Cancel
            </button>
            <button
              className="ppc-save"
              aria-label="Submit debug issue"
              disabled={!note.trim()}
              onClick={() => onCreate({ note: note.trim(), severity })}
            >
              Submit debug issue
            </button>
          </div>
        </div>
      </div>
    );
  }

  const t = ticket as Ticket;
  return (
    <div
      className="proto-pin-composer view"
      data-feedback-pin-composer
      style={{ left, top, width: COMPOSER_W + 40 }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="ppc-head">
        <span className="ppc-mono">PIN · {t.id}</span>
        <span className="ppc-by">{t.severity}</span>
        <button className="ppc-x" onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>
      <div className="ppc-body">
        <div className="ppc-orig">
          <div className="ppc-mono ppc-when">{new Date(t.createdAt).toLocaleString()}</div>
          <div className="ppc-note">{t.note}</div>
        </div>
        {(t.comments || []).length > 0 && (
          <div className="ppc-thread">
            {t.comments.map((c) => (
              <div key={c.id} className="ppc-comment">
                <div className="ppc-comment-head">
                  <span className="who">{c.who}</span>
                  <span className="when">{new Date(c.at).toLocaleString()}</span>
                </div>
                <div className="text">{c.text}</div>
              </div>
            ))}
          </div>
        )}
        <textarea
          ref={taRef}
          placeholder={`Reply as ${reporter || 'anonymous'}…`}
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && reply.trim()) {
              onAddComment(reply.trim());
              setReply('');
            }
          }}
        />
        <div className="ppc-foot">
          <button className="ppc-delete" onClick={onDelete} aria-label="Delete pin">
            Delete
          </button>
          <span className="ppc-mono">⌘↵ to reply</span>
          <button className="ppc-cancel" onClick={onClose}>
            Close
          </button>
          <button
            className="ppc-save"
            disabled={!reply.trim()}
            onClick={() => {
              onAddComment(reply.trim());
              setReply('');
            }}
          >
            Reply
          </button>
        </div>
      </div>
    </div>
  );
}
